import type { QueryKey } from '@tanstack/query-core';

import { InfiniteData, useInfiniteQuery, UseInfiniteQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAtomValue } from 'jotai';

import { useEmailApiClient } from '@/api/clients/useEmailApiClient.util.ts';
import { EmailThreadsLiveDto, EmailThreadsLocalDto } from '@/api/generated';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { folderStateAtom, useEmailsContext } from '@/views/emails/context/EmailsContext.ts';
import { useCurrentThreadsQueryKey } from '@/views/emails/util/useCurrentThreadsQueryKey.util.ts';

export type ThreadsInfiniteData = InfiniteData<UseReadEmailsResponse | undefined, ThreadsPageParam>;

export type ThreadsPageParam = {
  limit: number;
  offset: number;
};

export type UseReadEmailsResponse =
  | ({ discriminatorType: 'EmailThreadsLive' } & EmailThreadsLiveDto)
  | ({ discriminatorType: 'EmailThreadsLocal' } & EmailThreadsLocalDto);

type Return = UseInfiniteQueryResult<ThreadsInfiniteData, AxiosError>;

export const MAX_LOADED_PAGES = 20;

export const getLoadedThreadsEnd = ({ pageParams }: ThreadsInfiniteData): number => {
  const lastPageParam = pageParams.at(-1);
  return lastPageParam ? lastPageParam.offset + lastPageParam.limit : 0;
};

export const useReadEmails = (): Return => {
  const { emailReaderApi } = useEmailApiClient();
  const { userId } = useUserContext();
  const { emailsState } = useEmailsContext();
  const folderState = useAtomValue(folderStateAtom);
  const { selectedBox } = emailsState;
  const { pageSize, search, sortBy, sortOrder, unreadOnly } = folderState;
  const currentThreadsQueryKey = useCurrentThreadsQueryKey();

  const query = useInfiniteQuery<
    UseReadEmailsResponse | undefined,
    AxiosError,
    ThreadsInfiniteData,
    QueryKey,
    ThreadsPageParam
  >({
    enabled: Boolean(selectedBox?.folderId),
    gcTime: 60 * 1000,
    getNextPageParam: (lastPage, _allPages, { limit, offset }) => {
      if (!lastPage || lastPage.discriminatorType === 'EmailThreadsLive') {
        return undefined;
      }
      const nextOffset = offset + limit;
      if (nextOffset >= MAX_LOADED_PAGES * pageSize || nextOffset >= lastPage.total) {
        return undefined;
      }
      return { limit: pageSize, offset: nextOffset };
    },
    initialPageParam: { limit: pageSize, offset: 0 },
    queryFn: async ({ pageParam: { limit, offset }, signal }) => {
      if (!selectedBox) {
        throw new Error('No current box selected');
      }

      const { data } = await emailReaderApi.readEmails(
        userId,
        selectedBox.folderId,
        offset / limit,
        limit,
        unreadOnly === true ? true : undefined,
        sortBy,
        sortOrder,
        search,
        { signal },
      );
      return data;
    },
    queryKey: currentThreadsQueryKey,

    staleTime: 30 * 1000,
  });

  return query;
};
